/**
 * Export utility functions for generating CSV/Excel reports
 */

import * as XLSX from 'xlsx';

/**
 * Export validation errors/warnings to Excel file
 * @param {Array} validationResults - Array of validation results
 * @param {Array} dishes - Array of dish data (for row numbers)
 * @param {string} filename - Output filename (without extension)
 */
export const exportErrorReport = (validationResults, dishes, filename = 'import_error_report') => {
  // Prepare data for export
  const reportData = [];
  
  validationResults.forEach((result, index) => {
    const dish = dishes[index] || {};
    const rowNumber = index + 1; // 1-based row number
    
    // Add errors
    if (result.errors && result.errors.length > 0) {
      result.errors.forEach((error, errorIndex) => {
        reportData.push({
          'Row Number': rowNumber,
          'Dish Name (Armenian)': dish.nameHy || '',
          'Dish Name (English)': dish.nameEn || '',
          'Dish Name (Russian)': dish.nameRu || '',
          'Type': 'Error',
          'Field': error.field || '',
          'Message': error.message || ''
        });
      });
    }
    
    // Add warnings
    if (result.warnings && result.warnings.length > 0) {
      result.warnings.forEach((warning, warningIndex) => {
        reportData.push({
          'Row Number': rowNumber,
          'Dish Name (Armenian)': dish.nameHy || '',
          'Dish Name (English)': dish.nameEn || '',
          'Dish Name (Russian)': dish.nameRu || '',
          'Type': 'Warning',
          'Field': warning.field || '',
          'Message': warning.message || ''
        });
      });
    }
  });
  
  // If no errors/warnings, create a simple report
  if (reportData.length === 0) {
    reportData.push({
      'Row Number': '',
      'Dish Name (Armenian)': '',
      'Dish Name (English)': '',
      'Dish Name (Russian)': '',
      'Type': 'Info',
      'Field': '',
      'Message': 'No errors or warnings found'
    });
  }
  
  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(reportData);
  
  // Set column widths
  const colWidths = [
    { wch: 12 }, // Row Number
    { wch: 25 }, // Dish Name (Armenian)
    { wch: 25 }, // Dish Name (English)
    { wch: 25 }, // Dish Name (Russian)
    { wch: 10 }, // Type
    { wch: 20 }, // Field
    { wch: 50 }  // Message
  ];
  ws['!cols'] = colWidths;
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Error Report');
  
  // Generate Excel file and download
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * Export validation errors/warnings to CSV file
 * @param {Array} validationResults - Array of validation results
 * @param {Array} dishes - Array of dish data (for row numbers)
 * @param {string} filename - Output filename (without extension)
 */
export const exportErrorReportCSV = (validationResults, dishes, filename = 'import_error_report') => {
  // Prepare data for export
  const reportData = [];
  
  validationResults.forEach((result, index) => {
    const dish = dishes[index] || {};
    const rowNumber = index + 1; // 1-based row number
    
    // Add errors
    if (result.errors && result.errors.length > 0) {
      result.errors.forEach((error) => {
        reportData.push({
          'Row Number': rowNumber,
          'Dish Name (Armenian)': dish.nameHy || '',
          'Dish Name (English)': dish.nameEn || '',
          'Dish Name (Russian)': dish.nameRu || '',
          'Type': 'Error',
          'Field': error.field || '',
          'Message': error.message || ''
        });
      });
    }
    
    // Add warnings
    if (result.warnings && result.warnings.length > 0) {
      result.warnings.forEach((warning) => {
        reportData.push({
          'Row Number': rowNumber,
          'Dish Name (Armenian)': dish.nameHy || '',
          'Dish Name (English)': dish.nameEn || '',
          'Dish Name (Russian)': dish.nameRu || '',
          'Type': 'Warning',
          'Field': warning.field || '',
          'Message': warning.message || ''
        });
      });
    }
  });
  
  // If no errors/warnings, create a simple report
  if (reportData.length === 0) {
    reportData.push({
      'Row Number': '',
      'Dish Name (Armenian)': '',
      'Dish Name (English)': '',
      'Dish Name (Russian)': '',
      'Type': 'Info',
      'Field': '',
      'Message': 'No errors or warnings found'
    });
  }
  
  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(reportData);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Error Report');
  
  // Generate CSV (using Excel format but with CSV extension)
  const csv = XLSX.utils.sheet_to_csv(ws);
  
  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
